import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function HIPAAPrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-zinc-200 z-40 h-16 px-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5 text-zinc-950" />
        </button>
        <img src={AXIOM_LOGO} alt="Axiom" className="h-8" />
        <div className="w-5" />
      </header>

      <main className="pt-20 px-4 pb-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-zinc-950 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>HIPAA & Privacy Information</h1>
          <p className="text-sm text-zinc-500 mb-8">Your Health Data Protection Explained</p>

          <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h3 className="text-sm font-bold text-blue-900 uppercase mb-2">What is HIPAA?</h3>
              <p className="text-sm text-blue-800 leading-relaxed">
                HIPAA (Health Insurance Portability and Accountability Act) is a US federal law that protects sensitive patient health information from being disclosed without the patient's consent or knowledge.
              </p>
            </div>

            <section>
              <div className="flex items-start space-x-3 mb-3">
                <Lock className="w-6 h-6 text-zinc-950 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-bold text-zinc-950 mb-2">Data Encryption</h2>
                  <p className="text-sm text-zinc-700 leading-relaxed">
                    All your health information is encrypted both in transit and at rest:
                  </p>
                  <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1 mt-2">
                    <li><strong>Passwords:</strong> Hashed using bcrypt (irreversible)</li>
                    <li><strong>Sessions:</strong> Secured with JWT tokens + httpOnly cookies</li>
                    <li><strong>Database:</strong> Encrypted storage with restricted access</li>
                    <li><strong>Transmission:</strong> HTTPS/TLS encryption for all data transfers</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-start space-x-3 mb-3">
                <Shield className="w-6 h-6 text-zinc-950 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-bold text-zinc-950 mb-2">Access Controls</h2>
                  <p className="text-sm text-zinc-700 leading-relaxed">
                    Strict role-based access ensures only authorized personnel can view your data:
                  </p>
                  <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1 mt-2">
                    <li><strong>You:</strong> Full access to your own data</li>
                    <li><strong>SuperAdmins Only:</strong> Graeme Tudor & Seamus Campbell (encrypted data)</li>
                    <li><strong>No Third Parties:</strong> Zero access to external companies</li>
                    <li><strong>Criminal Justice:</strong> Unencrypted reports ONLY when legally mandated</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-start space-x-3 mb-3">
                <Eye className="w-6 h-6 text-zinc-950 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-bold text-zinc-950 mb-2">Data Minimization</h2>
                  <p className="text-sm text-zinc-700 leading-relaxed">
                    We only collect data necessary for your rehabilitation programme:
                  </p>
                  <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1 mt-2">
                    <li>Recovery progress (journal entries, check-ins)</li>
                    <li>Meeting attendance verification</li>
                    <li>Bot conversations for support</li>
                    <li>Week completion status</li>
                  </ul>
                  <p className="text-sm text-zinc-700 leading-relaxed mt-2">
                    We do NOT collect: browsing history, location tracking, biometric data (unless you choose to share) or unnecessary personal information.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-start space-x-3 mb-3">
                <FileText className="w-6 h-6 text-zinc-950 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-bold text-zinc-950 mb-2">International Compliance</h2>
                  <p className="text-sm text-zinc-700 leading-relaxed mb-2">
                    Beyond HIPAA, we comply with:
                  </p>
                  <div className="space-y-3">
                    <div className="bg-zinc-50 rounded p-3">
                      <h3 className="text-sm font-bold text-zinc-900 mb-1">GDPR (Europe)</h3>
                      <p className="text-xs text-zinc-600">General Data Protection Regulation ensures data protection and privacy for EU citizens.</p>
                    </div>
                    <div className="bg-zinc-50 rounded p-3">
                      <h3 className="text-sm font-bold text-zinc-900 mb-1">PIPEDA (Canada)</h3>
                      <p className="text-xs text-zinc-600">Personal Information Protection and Electronic Documents Act governs private sector data in Canada.</p>
                    </div>
                    <div className="bg-zinc-50 rounded p-3">
                      <h3 className="text-sm font-bold text-zinc-900 mb-1">Australian Privacy Principles</h3>
                      <p className="text-xs text-zinc-600">13 principles governing data handling for Australian organizations.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">Breach Notification</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                In the unlikely event of a data breach, we will:
              </p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1 mt-2">
                <li>Notify affected users within 72 hours</li>
                <li>Report to relevant authorities (as required by law)</li>
                <li>Take immediate action to secure systems</li>
                <li>Provide guidance on protecting your information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">Your Rights Under HIPAA</h2>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1">
                <li><strong>Right to Access:</strong> Request copies of your health records</li>
                <li><strong>Right to Amend:</strong> Request corrections to inaccurate data</li>
                <li><strong>Right to Accounting:</strong> Know who has accessed your data</li>
                <li><strong>Right to Restrict:</strong> Limit certain uses of your data</li>
                <li><strong>Right to Confidential Communications:</strong> Request private channels</li>
              </ul>
              <p className="text-sm text-zinc-700 leading-relaxed mt-3">
                To exercise these rights: <strong>support@axiomtudor8.com</strong>
              </p>
            </section>

            <section className="bg-yellow-50 border border-yellow-200 rounded p-4">
              <h3 className="text-sm font-bold text-yellow-900 uppercase mb-2">Important Note</h3>
              <p className="text-sm text-yellow-800 leading-relaxed">
                While we implement HIPAA-level security measures, full HIPAA compliance requires hosting on certified servers. We are working towards complete HIPAA certification. Your data remains protected under international privacy laws regardless of certification status.
              </p>
            </section>
          </div>

          <Button
            onClick={() => navigate(-1)}
            className="mt-6 bg-zinc-950 text-white hover:bg-zinc-800 rounded-md h-12"
          >
            Back
          </Button>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default HIPAAPrivacyPage;
